import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { StyledMenuItem } from './MenuItem.styles';

import { menuDefaultProps } from '../Menu.defaultProps';

import { MenuItemProps } from './MenuItem.types';

import { MenuItemList } from '../MenuItemList';

const MenuItem = forwardRef(
  (
    {
      id,
      beforeLabelSlot,
      afterLabelSlot,
      childrenItemList,
      depth = 0,
      itemTag: ItemTag = 'div',
      href,
      labelSlot,
      display,
      renderLink,
      currentLocationPath,
      componentClassname = 'menu-item',
      customClassnames = '',
      wayToDisplayChildrenByDropdown = menuDefaultProps.wayToDisplayChildrenByDropdown,
      areChildrenDisplayedByDropdown = menuDefaultProps.areChildrenDisplayedByDropdown,
      toggleSlot = menuDefaultProps.toggleSlot,
      currentPageLabel = menuDefaultProps.currentPageLabel,
      onOpenSubMenu = menuDefaultProps.onOpenSubMenu,
      onCloseSubMenu = menuDefaultProps.onCloseSubMenu,
      ...rest
    }: MenuItemProps,
    ref,
  ) => {
    const submenuItemListRef = useRef(null);
    const [subMenuIsOpen, setSubMenuIsOpen] = useState(
      !areChildrenDisplayedByDropdown,
    );
    const [
      menuWayToDisplayChildrenByDropdown,
      setMenuWayToDisplayChildrenByDropdown,
    ] = useState(wayToDisplayChildrenByDropdown);

    if (!id) {
      id = labelSlot.toString();
    }

    useImperativeHandle(ref, () => ({
      closeSubMenu() {
        closeSubMenu();
      },
    }));

    useEffect(() => {
      setMenuWayToDisplayChildrenByDropdown(
        display === 'desktop' ? wayToDisplayChildrenByDropdown : 'click',
      );
    }, [display, wayToDisplayChildrenByDropdown]);

    const isCurrentPage = (link: string) => {
      if (!link || !currentLocationPath) {
        return false;
      }
      let linkRawUrl = link.split('#')[0].split('?')[0];
      if (linkRawUrl.slice(-1) !== '/') {
        linkRawUrl += '/';
      }
      let localLocationPath = currentLocationPath;
      if (localLocationPath.slice(-1) !== '/') {
        localLocationPath += '/';
      }
      return linkRawUrl === localLocationPath;
    };

    const getMenuItemLabelRender = () => {
      if (href && !isCurrentPage(href)) {
        return renderLink ? (
          renderLink({
            href: href,
            children: labelSlot,
            additionalParams: {
              className: `${componentClassname}__item-link`,
            },
          })
        ) : (
          <a className={`${componentClassname}__item-link`} href={href}>
            {labelSlot}
          </a>
        );
      }
      return (
        <>
          <span
            className={`${componentClassname}__item-no-link ${
              isCurrentPage(href) ? `${componentClassname}__current-link` : ''
            }`}
          >
            {labelSlot}
          </span>
          {href && isCurrentPage(href) && (
            <span className="sr-only">{`(${currentPageLabel})`}</span>
          )}
        </>
      );
    };

    const [menuItemLabelRender, setMenuItemLabelRender] = useState(
      getMenuItemLabelRender(),
    );
    useEffect(() => {
      setMenuItemLabelRender(getMenuItemLabelRender());
    }, [currentLocationPath]);

    const getToggleElementRender = () => {
      return menuWayToDisplayChildrenByDropdown === 'click' ? (
        <button
          type="button"
          onClick={handleToggleElementClick}
          className={`toggle-submenu-element`}
        >
          {toggleSlot}
        </button>
      ) : (
        <span className={`toggle-submenu-element`}>{toggleSlot}</span>
      );
    };

    const handleToggleElementClick = () => {
      if (subMenuIsOpen) {
        closeSubMenu();
      } else {
        openSubMenu();
      }
    };

    const handleElementMouseEnter = () => {
      openSubMenu();
    };

    const handleElementMouseLeave = () => {
      closeSubMenu();
    };

    const openSubMenu = () => {
      if (areChildrenDisplayedByDropdown && !subMenuIsOpen) {
        setSubMenuIsOpen(true);
        onOpenSubMenu(id);
      }
    };
    const closeSubMenu = () => {
      if (areChildrenDisplayedByDropdown && subMenuIsOpen) {
        setSubMenuIsOpen(false);
        onCloseSubMenu(id);
        if (submenuItemListRef.current) {
          submenuItemListRef.current.closeSubMenu();
        }
      }
    };

    let itemEvents = {};
    if (wayToDisplayChildrenByDropdown === 'hover') {
      itemEvents = {
        onMouseEnter: handleElementMouseEnter,
        onMouseLeave: handleElementMouseLeave,
      };
    }

    return (
      <StyledMenuItem
        as={ItemTag}
        className={`${componentClassname} ${customClassnames} ${
          subMenuIsOpen ? `${componentClassname}--is-open` : ''
        } ${
          childrenItemList &&
          childrenItemList.itemList &&
          childrenItemList.itemList?.length
            ? `${componentClassname}--with-children`
            : ''
        }`}
        data-depth={depth}
        {...itemEvents}
      >
        <div
          className={`${componentClassname}__label-wrapper`}
          data-depth={depth}
        >
          {beforeLabelSlot && (
            <span className={`${componentClassname}__before-label`}>
              {beforeLabelSlot}
            </span>
          )}
          <span
            className={`${componentClassname}__label`}
            aria-expanded={
              areChildrenDisplayedByDropdown && childrenItemList
                ? subMenuIsOpen
                : null
            }
            data-depth={depth}
          >
            {menuItemLabelRender}
          </span>
          {childrenItemList &&
            childrenItemList.itemList &&
            areChildrenDisplayedByDropdown &&
            getToggleElementRender()}
          {afterLabelSlot && (
            <span className={`${componentClassname}__after-label`}>
              {afterLabelSlot}
            </span>
          )}
        </div>
        {childrenItemList && childrenItemList.itemList && (
          <div className={`${componentClassname}__submenu`} data-depth={depth}>
            <MenuItemList
              ref={submenuItemListRef}
              {...rest}
              {...childrenItemList}
              display={display}
              depth={depth + 1}
              wayToDisplayChildrenByDropdown={
                menuWayToDisplayChildrenByDropdown
              }
              renderLink={renderLink}
              currentLocationPath={currentLocationPath}
              areChildrenDisplayedByDropdown={
                childrenItemList.areChildrenDisplayedByDropdown ??
                areChildrenDisplayedByDropdown
              }
              currentPageLabel={
                childrenItemList.currentPageLabel ?? currentPageLabel
              }
              toggleSlot={childrenItemList.toggleSlot ?? toggleSlot}
              onOpenSubMenu={childrenItemList.onOpenSubMenu ?? onOpenSubMenu}
              onCloseSubMenu={childrenItemList.onCloseSubMenu ?? onCloseSubMenu}
            />
          </div>
        )}
      </StyledMenuItem>
    );
  },
);
MenuItem.displayName = 'MenuItem';

export default MenuItem;
